import { useEffect } from "react"
import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from "react-router-dom"
import { useComputed, useSignal, useSignalEffect } from "@preact/signals-react"
import _ from "lodash-es"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Dropdown from 'react-bootstrap/Dropdown'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Nav from 'react-bootstrap/Nav'
import Pagination from 'react-bootstrap/Pagination'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'

import ConfirmationModal from "../../components/ConfirmationModal"
import DebugAccordion from "../../components/DebugAccordion"
import Icon from "../../components/Icon"
import PageTitle from "../../components/PageTitle.jsx"
import { NotificationTypes, addNotification } from "../../stores/notification-store"

import useConfigTemplates from "../../hooks/useConfigTemplates.js"
import { CreateConfigTemplate, DeleteConfigTemplate } from "../../utils/api.js"

export default function ConfigTemplateList() {

    const navigate = useNavigate();
    const { beId, componentId } = useParams()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { config, accessToken, userPermissionsSummary, accordionData: sidebarAccordionData  } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewConfigTemplates) {
            addNotification('You are not authorized to view UTG Config Templates for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value])
    
    const canEditSignal = useComputed(() => {
        return userPermissionsSummary?.value?.canEditConfigTemplates
    })

    const { configTemplates, loading, error, modified, fetchData } = useConfigTemplates(beId, config, accessToken)
    const configTemplateNames = useComputed(() => {
        if (configTemplates.value) {
            return configTemplates.value.map((p) => p.name)
        }
        return []
    })

    const currentPage = useSignal(1)
    const rowsPerPageSignal = useSignal(parseInt(window.localStorage.getItem('pagesize')) || 250)
    const filterString = useSignal("")
    const sortColumn = useSignal('')

    const SortDirectionType = {
        ASCENDING: 1,
        DESCENDING: 2
    }
    const sortDirection = useSignal(SortDirectionType.DESCENDING)
    
    useSignalEffect(() => {
        window.localStorage.setItem('pagesize', rowsPerPageSignal.value)
    })

    const filteredData = useComputed(() => {
        if (!filterString.value) {
            return configTemplates.value ? [...configTemplates.value] : []
        }

        // Convert the filter to lowercase
        const lowerCaseFilter = filterString.value.toLowerCase()

        // Filter the data based on the filter text
        return configTemplates.value.filter(item => {

            // Limit search to these fields
            let searchData = {
                name: item.name
            }

            return Object.values(searchData).some(value => {
                if (value && (typeof (value) == 'string')) {
                    return value.toString().toLowerCase().includes(lowerCaseFilter)
                }
            })
        })
    })

    const sortedData = useComputed(() => {

        // just return filtered data if no sorting
        if (!sortColumn.value.length) {
            return [...filteredData.value]
        }

        let _sortedData = [...filteredData.value]
        return _sortedData.sort((a, b) => {
            let _a = _.get(a, sortColumn.value, null)
            let _b = _.get(b, sortColumn.value, null)

            // Convert nulls to empty strings for sorting purposes
            _a = _a ? _a : ''
            _b = _b ? _b : ''

            // convert values to lowercase
            if (_a && _a.length) {
                _a = _a.toLowerCase()
            }
            if (_b && _b.length) {
                _b = _b.toLowerCase()
            }

            if (sortDirection.value == SortDirectionType.DESCENDING) {
                if (_a < _b) return -1
                if (_a > _b) return 1
                return 0
            }
            else {
                if (_a > _b) return -1
                if (_a < _b) return 1
                return 0
            }
        })
        return _sortedData
    })

    const totalPages = useComputed(() => {
        return Math.ceil(filteredData.value.length / parseInt(rowsPerPageSignal.value, 10))
    })

    const displayData = useComputed(() => {
        // Calculate the starting and ending indexes for pagination
        const startIndex = (currentPage.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value

        return sortedData.value.slice(startIndex, endIndex)
    })
    const startDisplayRowNumber = useComputed(() => {
        return ((currentPage.value - 1) * rowsPerPageSignal.value) + 1
    })
    const endDisplayRowNumber = useComputed(() => {
        const startIndex = (currentPage.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value
        return endIndex < filteredData.value.length ? endIndex : filteredData.value.length
    })

    const accordionData = [
        { name: 'Config Templates', data: configTemplates },
        { name: 'Display Data', data: displayData },
        { name: 'Sorted Data', data: sortedData },
        { name: 'Filtered Data', data: filteredData },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]

    // Delete profile modal
    const showModalDelete = useSignal(false)
    const deleteConfigTemplateItem = useSignal({})
    const handleDeleteConfigTemplateShowModal = item => {
        if (item) {
            deleteConfigTemplateItem.value = item
            showModalDelete.value = true
        }
    }
    const handleSubmitModalDelete = async () => {
        showModalDelete.value = false
        try {
            const configTemplateResult = await DeleteConfigTemplate(beId, deleteConfigTemplateItem.value.id, config, accessToken)
            modified.value = true; // flag useConfigTemplates to reload data when called again
            addNotification('Config template successfully deleted', 'success', true)  
            await fetchData(beId, config, accessToken)
        } catch (error) {
            addNotification(`Unable to delete profile. ${error}`)
        }
    }

    // New config template modal
    const showModalNew = useSignal(false)
    const newConfigTemplateName = useSignal('') // server enforces uniqueness within be
    const nameAlreadyUsed = useComputed(() => {        
        return configTemplateNames.value.length && configTemplateNames.value.includes(newConfigTemplateName.value)
    })
    const disableSubmit = useComputed(() => {
        return (newConfigTemplateName.value?.length == 0 || nameAlreadyUsed.value)
    })
    const showSpinner = useSignal(false)
    const handleCloseModalNew = () => showModalNew.value = false
    const handleSubmitModalNew = async () => {
        showModalNew.value = false
        showSpinner.value = true

        try {
            const configTemplate = {
                name: newConfigTemplateName.value,
                businessEntityId: beId,
                data: JSON.stringify({})
            }

            const configTemplateResult = await CreateConfigTemplate(beId, configTemplate, config, accessToken)
            modified.value = true; // flag useConfigTemplates to reload data when called again
            addNotification('Config Template successfully created', 'success', true)            
            navigate(`./${configTemplateResult.id}`)
        } catch (error) {
            addNotification(`Unable to create config template. ${error}`)
        }
        finally {
            showSpinner.value = false
        }
    }

    // Action toolbar

    const handlePageSizeSelect = (v) => {
        currentPage.value = 1
        rowsPerPageSignal.value = parseInt(v, 10)
    }
    const handleFilterStringChange = (e) => {
        currentPage.value = 1
        filterString.value = e.currentTarget.value
    }
    const handleCopyClipboard = item => {
        if (item) {
            // place the data on the users clipboard if the browser supports it
            if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(JSON.stringify(item, null, 2));
                addNotification(`The data for ${item.name} has been copied to your clipboard.`, NotificationTypes.SUCCESS, false, 'Copy Succeeded')
            }
            else {
                addNotification('This browser does not support copying data to the clipboard.', NotificationTypes.INFO, false, 'Copy Failed')
            }
        }
    }

    const handleHeaderTableClick = datafield => {
        // if already sorted on this column then toggle sort direction
        if (datafield == sortColumn.value) {
            sortDirection.value = sortDirection.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
            return
        }

        // update sort column - the computed functions will then run and render the sorted data
        sortColumn.value = datafield
    }

    function TableRows({ items }) {
        if (items && items.length) {
            const rows = items.map(item => {
                return <TableRow item={item} key={`${item.id}`} />
            })

            return (
                <tbody>{rows}</tbody>
            )
        }
        else if (items) {
            return (
                <tbody><tr><td colSpan="23" className="text-center">No config templates found</td></tr></tbody>
            )
        }
    }

    function TableRow({ item }) {
        return (
            <tr>
                <td>
                    <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" id="actions">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-gear-fill" viewBox="0 0 16 16">
                                <path d="M9.405 1.05c-.413-1.4-2.397-1.4-2.81 0l-.1.34a1.464 1.464 0 0 1-2.105.872l-.31-.17c-1.283-.698-2.686.705-1.987 1.987l.169.311c.446.82.023 1.841-.872 2.105l-.34.1c-1.4.413-1.4 2.397 0 2.81l.34.1a1.464 1.464 0 0 1 .872 2.105l-.17.31c-.698 1.283.705 2.686 1.987 1.987l.311-.169a1.464 1.464 0 0 1 2.105.872l.1.34c.413 1.4 2.397 1.4 2.81 0l.1-.34a1.464 1.464 0 0 1 2.105-.872l.31.17c1.283.698 2.686-.705 1.987-1.987l-.169-.311a1.464 1.464 0 0 1 .872-2.105l.34-.1c1.4-.413 1.4-2.397 0-2.81l-.34-.1a1.464 1.464 0 0 1-.872-2.105l.17-.31c.698-1.283-.705-2.686-1.987-1.987l-.311.169a1.464 1.464 0 0 1-2.105-.872zM8 10.93a2.929 2.929 0 1 1 0-5.86 2.929 2.929 0 0 1 0 5.858z" />
                            </svg>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleCopyClipboard(item)}>Copy Row as JSON</Dropdown.Item>
                            {canEditSignal.value && <Dropdown.Item onClick={() => handleDeleteConfigTemplateShowModal(item)}><Icon icon="trash" width={16} height={16}></Icon> Delete config template</Dropdown.Item>}
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
                <td><Link to={`./${item.id}`}>{item.name}</Link></td>
                <td>{item.businessEntityId}</td>
            </tr>
        )
    }

    function PaginationItems() {
        let items = []
        for (let i = 1; i <= totalPages.value; i++) {
            items.push(
                <Pagination.Item key={i} active={i === currentPage.value} onClick={() => currentPage.value = i}>{i}</Pagination.Item>
            )
        }
        return items
    }

    function TableColumnHeader({ title, sortable, datafield }) {
        // non-sortable columns do not have click handlers
        if (!sortable) {
            return (
                <th>{title}</th>
            )
        }

        // sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
        if (datafield != sortColumn.value) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
            )
        }

        // datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
        if (sortDirection.value == SortDirectionType.DESCENDING) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                    <Icon icon="caret-down" width="16" height="16"></Icon>
                </th>
            )
        }

        // else must be ascending
        return (
            <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                <Icon icon="caret-up" width="16" height="16"></Icon>
            </th>
        )
    }

    if (loading.value || !configTemplates.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    return (
        <>
            <PageTitle title='Config Template List' canEditSignal={canEditSignal} />
            <Nav className="justify-content-between px-3 py-1 my-2 bg-light">
                <DropdownButton variant="outline-secondary" className="me-auto" title={`${rowsPerPageSignal} records per page`} id="pageSize-dropdown" onSelect={handlePageSizeSelect}>
                    <Dropdown.Item eventKey="10">10</Dropdown.Item>
                    <Dropdown.Item eventKey="25">25</Dropdown.Item>
                    <Dropdown.Item eventKey="50">50</Dropdown.Item>
                    <Dropdown.Item eventKey="250">250</Dropdown.Item>
                    <Dropdown.Item eventKey="500">500</Dropdown.Item>
                    <Dropdown.Item eventKey="1000">1000</Dropdown.Item>
                </DropdownButton>
                <Nav.Item className="mt-1 me-2">
                    <Form.Control type="text" placeholder="Search" size="sm" htmlSize="75" onChange={handleFilterStringChange} />
                </Nav.Item>
                {canEditSignal.value && <Nav.Item className="mt-1">
                    <Button size="sm" variant="outline-secondary" className="d-flex align-items-center justify-content-center" onClick={() => showModalNew.value = true}><Icon icon='plus-circle' width="16" height="16" ></Icon>&nbsp;New</Button>
                </Nav.Item>}
            </Nav>
            <Table responsive striped hover size="sm" className="cc-table">
                <thead>
                    <tr>
                        <TableColumnHeader title={'Action'} sortable={false}></TableColumnHeader>
                        <TableColumnHeader title={'Config Template Name'} sortable={true} datafield={'name'}></TableColumnHeader>
                        <TableColumnHeader title={'Parent Account'} sortable={true} datafield={'businessEntityId'}></TableColumnHeader>
                    </tr>
                </thead>
                <TableRows items={displayData.value} />
                <tfoot><tr><td className="fs-6" colSpan={19}>Displaying {startDisplayRowNumber.value}-{endDisplayRowNumber.value} of {filteredData.value.length}</td></tr></tfoot>
            </Table>
            <Pagination className="px-3 d-flex flex-wrap" size="sm"><PaginationItems /></Pagination>
            <DebugAccordion data={accordionData} debug={debug} />

            <Modal show={showModalNew.value} onHide={() => showModalNew.value = false}>
                <Modal.Header closeButton>
                    <Modal.Title>New Config Template</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
                            <Form.Label>Config Template Name</Form.Label>
                            <Form.Control
                                type="text"
                                required
                                onInput={(e) => newConfigTemplateName.value = e.target.value}
                                autoFocus
                                value={newConfigTemplateName}
                            />
                            <p className="mt-1" hidden={!nameAlreadyUsed.value}>Config template name must be unique</p>
                        </Form.Group>                    
                    </Form>               
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => showModalNew.value = false}>
                        Cancel
                    </Button>
                    <Button disabled={disableSubmit.value} variant="primary" onClick={handleSubmitModalNew}>
                        Add New
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showModalDelete.value} onHide={() => showModalDelete.value = false}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Config Template</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Deleting a config template is a permanent action that cannot be reversed. </p>
                    <p>Are you sure you want to delete {deleteConfigTemplateItem.value.name}?</p>     
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => showModalDelete.value = false}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleSubmitModalDelete}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}